import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNotificacao } from './NotificacaoContext';
import * as planosPagamentoService from '../services/planosPagamentoService';

const PlanosPagamentoContext = createContext({});

export const PlanosPagamentoProvider = ({ children }) => {
  const [planosPagamento, setPlanosPagamento] = useState([]);
  const [loading, setLoading] = useState(false);
  const { adicionarNotificacao } = useNotificacao();

  const carregarPlanosPagamento = useCallback(async () => {
    try {
      setLoading(true);
      const data = await planosPagamentoService.listarPlanosPagamento();
      setPlanosPagamento(data?.data || []); // Adicione validação
    } catch (error) {
      console.error('Erro detalhado:', error);
      setPlanosPagamento([]); // Garante um estado válido mesmo em caso de erro
      adicionarNotificacao('Erro ao carregar planos de pagamento', 'error');
    } finally {
      setLoading(false);
    }
  }, [adicionarNotificacao]); // Adiciona adicionarNotificacao como dependência

  const criarPlanoPagamento = useCallback(async (planoPagamentoData) => {
    try {
      setLoading(true);
      const novoPlanoPagamento = await planosPagamentoService.criarPlanoPagamento(planoPagamentoData);
      setPlanosPagamento(planos => [...planos, novoPlanoPagamento]);
      adicionarNotificacao('Plano de pagamento criado com sucesso', 'success');
      return novoPlanoPagamento;
    } catch (error) {
      adicionarNotificacao('Erro ao criar plano de pagamento', 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [adicionarNotificacao]);

  const atualizarPlanoPagamento = useCallback(async (id, planoPagamentoData) => {
    try {
      setLoading(true);
      const planoAtualizado = await planosPagamentoService.atualizarPlanoPagamento(id, planoPagamentoData);
      setPlanosPagamento(planos => 
        planos.map(plano => plano.id === id ? planoAtualizado : plano)
      );
      adicionarNotificacao('Plano de pagamento atualizado com sucesso', 'success');
      return planoAtualizado;
    } catch (error) {
      adicionarNotificacao('Erro ao atualizar plano de pagamento', 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [adicionarNotificacao]);

  const excluirPlanoPagamento = useCallback(async (id) => {
    try {
      setLoading(true);
      await planosPagamentoService.excluirPlanoPagamento(id);
      setPlanosPagamento(planos => planos.filter(plano => plano.id !== id));
      adicionarNotificacao('Plano de pagamento excluído com sucesso', 'success');
    } catch (error) {
      adicionarNotificacao('Erro ao excluir plano de pagamento', 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [adicionarNotificacao]);

  const obterPlanoPagamento = useCallback(async (id) => {
    try {
      setLoading(true);
      const plano = await planosPagamentoService.obterPlanoPagamento(id);
      return plano;
    } catch (error) {
      adicionarNotificacao('Erro ao obter plano de pagamento', 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [adicionarNotificacao]);

  return (
    <PlanosPagamentoContext.Provider
      value={{
        planosPagamento,
        loading,
        carregarPlanosPagamento,
        criarPlanoPagamento,
        atualizarPlanoPagamento,
        excluirPlanoPagamento,
        obterPlanoPagamento
      }}
    >
      {children}
    </PlanosPagamentoContext.Provider>
  );
};

PlanosPagamentoProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const usePlanosPagamento = () => {
  const context = useContext(PlanosPagamentoContext);
  if (!context) {
    throw new Error('usePlanosPagamento deve ser usado dentro de um PlanosPagamentoProvider');
  }
  return context;
};

export default PlanosPagamentoContext;